//
// Copyright (c) 2020 Nutanix Inc. All rights reserved.
//
// ChangePassword - Contains the components that make up the "ChangePassword" page. This will
// the user password when the user provides the old and new password.
//
import React from 'react';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    StackingLayout,
    FormItemInput,
    FormItemInputPassword,
    Button,
    Title,
    Popover,
    UnorderedList,
    Alert,
} from '@nutanix-ui/prism-reactjs';
import { apiFetchRequest } from '../../actions';
import { LoginLayout } from '../../components/Common';
import { STORAGE_USERNAME } from '../../constants';
import { PATH_URL } from '../../constant';
import './ChangePassword.less';

// CHANGE TO ntnx-I18n library later
import getI18nService from '../../i18n/I18n';
import { apiRoot } from '../../config';

const i18n = getI18nService();

const OLD_PW_ID = 'oldPassword';
const NEW_PW_ID = 'newPassword';
const CONFIRM_PW_ID = 'confirmPassword';
const VALID_USER_PASSWORD =
    /((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[-@$#!%^&*()_+|~=`{}[\]:";'<>,./]).{8,40})/;

export class ChangePassword extends React.Component {
    static propTypes = {
        // props for unit test
        onClickHandler: PropTypes.func,
        history: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            // Get username from session storage when component mount
            username: this.getUsernameToSession(),
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
            showError: false,
            errorMessage: '',
            hasInvalidPassword: false,
        };
    }

    render() {
        const {
            oldPassword,
            newPassword,
            confirmPassword,
            showError,
            hasInvalidPassword,
            errorMessage,
        } = this.state;

        // texts to show in the password restrictions popOver
        const passwordRestrictions = [
            i18n.t('ChangePassword.passwordLength'),
            i18n.t('ChangePassword.oneLowerCase'),
            i18n.t('ChangePassword.oneUpperCase'),
            i18n.t('ChangePassword.oneNumber'),
            i18n.t('ChangePassword.oneSpecialCharacter'),
            i18n.t('ChangePassword.differentPassword'),
        ];

        // password restrictions popOver: invalid
        const popOverContent = (
            <StackingLayout data-test="password-popover" padding="20px">
                <Title size="h3">{i18n.t('ChangePassword.popOverTitle')}</Title>
                <UnorderedList className="unordered-list" data={passwordRestrictions} />
            </StackingLayout>
        );

        // password change Form
        const passwordForm = (
            <form id="password-change-form" onSubmit={this.handleFormSubmit}>
                <StackingLayout className="form-container" itemGap="XL">
                    {showError && (
                        <Alert
                            data-test="change-password-alert-message"
                            showCloseIcon={false}
                            type={Alert.TYPE.ERROR}
                            message={errorMessage}
                        />
                    )}
                    <Title size="h3" role="title">
                        {i18n.t('ChangePassword.createPasswordLabel')}
                    </Title>
                    <FormItemInput
                        id={OLD_PW_ID}
                        data-test="changePasswordInputOldPwd"
                        value={oldPassword}
                        label={i18n.t('ChangePassword.existingPasswordLabel')}
                        type="password"
                        placeholder="Type old password"
                        onChange={this.handleOldPwInputChange}
                    />
                    <Popover
                        className="popOver"
                        content={popOverContent}
                        placement="right"
                        key="newPassword-popover"
                    >
                        <FormItemInputPassword
                            id={NEW_PW_ID}
                            data-test="changePasswordInputNewPwd"
                            value={newPassword}
                            error={!!newPassword && !this.validatePassword(newPassword)}
                            type="password"
                            label={i18n.t('ChangePassword.newPasswordLabel')}
                            placeholder={i18n.t('ChangePassword.typePasswordLabel')}
                            onChange={this.handleInputChange}
                        />
                    </Popover>
                    <Popover
                        className="popOver"
                        content={popOverContent}
                        placement="right"
                        key="confirmPassword-popover"
                    >
                        <FormItemInputPassword
                            id={CONFIRM_PW_ID}
                            data-test="changePasswordInputConfirmPwd"
                            value={confirmPassword}
                            error={!!confirmPassword && !this.validatePasswordToSave()}
                            label={i18n.t('ChangePassword.confirmNewPasswordLabel')}
                            placeholder={i18n.t('ChangePassword.retypePasswordLabel')}
                            onChange={this.handleInputChange}
                        />
                    </Popover>
                    <Button
                        id="changePasswordSubmit"
                        data-test="changePasswordButtonSubmit"
                        data-testid="changePasswordButtonSubmit"
                        fullWidth={true}
                        htmlType="submit"
                        disabled={this.disableSubmit(
                            oldPassword,
                            newPassword,
                            confirmPassword,
                            hasInvalidPassword
                        )}
                    >
                        {i18n.t('ChangePassword.submit')}
                    </Button>
                </StackingLayout>
            </form>
        );

        return <LoginLayout form={passwordForm} leftTitle={i18n.t('LoginLayout.reimagineText')} />;
    }

    /**
     * Handles form submit
     * @param {Object} e - input event
     * @returns {void}
     */
    handleFormSubmit = e => {
        e.preventDefault();
        const handleClick = this.props.onClickHandler || this.handleSubmitChangePassword;
        handleClick();
    };

    /**
     * Handles input change for old password
     * @param {Object} evt - input event
     * @returns {void}
     */
    handleOldPwInputChange = evt => {
        const { value, id } = evt.target;

        // Update for oldPassword
        this.setState({
            [id]: value,
            showError: false,
        });
    };

    /**
     * Handles input change for new / confirm password
     * Need to
     * @param {Object} evt - input event
     * @returns {void}
     */
    handleInputChange = evt => {
        const { value, id } = evt.target;
        const { newPassword, confirmPassword } = this.state;

        // Get if the new input is valid password
        const isValidPassword = this.validatePassword(value);
        // Alert is visible for NEW_PW_ID or CONFIRM_PW_ID
        // If new value as same as value of other input field
        const isSamePassword = this.confirmIsSamePassword(id, newPassword, confirmPassword, value);

        if (isSamePassword && isValidPassword) {
            // Both newPassword and confirmPassword are valid and same
            // Update state to set both alert as false
            this.setState({
                [id]: value,
                hasInvalidPassword: false,
                showError: false,
            });
        } else {
            // Update state
            this.setState({
                [id]: value,
                hasInvalidPassword: !(isValidPassword && isSamePassword),
                showError: false,
            });
        }
    };

    /**
   * Confirm Same as other input password
   * @param {String} id - id of new input field.
   * @param {String} newPassword - string of newPassword.
   * @param {String} confirmPassword - string of confirmPassword.
   * @param {String} newValue - new Input value
   @returns {Boolean} - current user input password
   **/
    confirmIsSamePassword = (id, newPassword, confirmPassword, newValue) => {
        if (id === NEW_PW_ID) {
            // Current input new password
            return confirmPassword === '' || newValue === confirmPassword;
        }
        // Current input is confirmPassword
        return newPassword === newValue;
    };

    /**
   * Get username from session storage
   @returns {String} - username from session storage
   **/
    getUsernameToSession = () => {
        return sessionStorage.getItem(STORAGE_USERNAME || 'username') || '';
    };

    /**
     * Disable submit button if old password / new password / confirmPassword
     * aren't valid
     * @param {String} oldPassword - string of oldPassword.
     * @param {String} newPassword - string of newPassword.
     * @param {String} confirmPassword - string of confirmPassword.
     * @param {Boolean} hasInvalidPassword - boolean of newPassword has no valid alert.
     * @returns {boolean} should disable submit button
     **/
    disableSubmit = (oldPassword, newPassword, confirmPassword, hasInvalidPassword) => {
        const unFilledInput = oldPassword === '' || newPassword === '' || confirmPassword === '';
        return unFilledInput || hasInvalidPassword;
    };

    /**
     * Handles submit for change password API request
     * @returns {void}
     */
    handleSubmitChangePassword = async () => {
        const { oldPassword, newPassword, username } = this.state;
        const { history } = this.props;

        const { valid, errorMsg } = this.validatePasswordToSave();

        if (valid) {
            const userData = {
                username,
                old_password: oldPassword,
                new_password: newPassword,
            };

            const response = await fetch(`${apiRoot}/users/password`, {
                method: 'put',
                body: JSON.stringify(userData),
            });

            if (response.status >= 400) {
                // Handle 400 and other errors
                const jsonResponse = await response.json();
                this.setState({
                    showError: true,
                    errorMessage:
                        jsonResponse.message || i18n.t('ErrorMessages.unknownErrorMessage'),
                });
            } else {
                // Redirect to /login page after successful change password
                history.push({
                    pathname: PATH_URL.LOGIN,
                    state: {
                        changePWSuccess: true,
                    },
                });
            }
        } else {
            this.setState({
                showError: true,
                errorMessage: errorMsg,
            });
        }
    };

    /**
     * Validates newPassword complexity and to be length of newPassword be at longer than 8 characters
     * @param {String} password - password
     * @returns {Boolean} - true if valid or false if invalid
     */
    validatePassword = password => {
        const regex = VALID_USER_PASSWORD;
        return regex.test(password) && password.length >= 8;
    };

    /**
     * Validates newPassword complexity, validates new password to be differente than old one
     * validates confirmPassword to be the same as newPassword and length of confirmPassword
     * be at longer than 8 characters
     * @returns {Boolean} - true if valid or false if invalid
     */
    validatePasswordToSave = () => {
        const { oldPassword, newPassword, confirmPassword } = this.state;

        const valid = this.validatePassword(newPassword);
        let errorMsg = i18n.t('ChangePassword.notSamePassword');
        if (
            valid &&
            oldPassword &&
            oldPassword !== newPassword &&
            newPassword === confirmPassword &&
            confirmPassword.length >= 8
        ) {
            return { valid: true, errorMsg: '' };
        }

        if (oldPassword === newPassword) {
            errorMsg = i18n.t('ChangePassword.differentOldPassword');
            return { valid: false, errorMsg };
        }

        return { valid: false, errorMsg };
    };
}

const mapStateToProps = state => {
    return {
        application: state.application,
    };
};

const enhancedChangePassword = compose(
    withRouter,
    connect(mapStateToProps, { apiFetchRequest })
)(ChangePassword);

export default enhancedChangePassword;
